import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useAppSelector } from './useAppSelector.hook';

export interface CentrifugoManagerValue {
  events: any;
  dispatch: Function;
  subscribe: Function;
  unsubscribe: Function;
}

const CentrifugoManagerContext = React.createContext<CentrifugoManagerValue>(
  {} as any,
);

export interface CentrifugoManagerProps {
  children?: React.ReactNode;
}

export enum CentrifugoEventType {
  META_EVENT = 'META_EVENT',
  NOTIFICATION_EVENT = 'NOTIFICATION_EVENT',
  TRIGGER_EVENT = 'TRIGGER_EVENT',
}

const CentrifugoManagerHook = ({ children }: CentrifugoManagerProps) => {
  const [events, setEvent] = useState({});

  const value = useMemo<CentrifugoManagerValue>(() => {
    const dispatch = (event: CentrifugoEventType, data: any) => {
      // @ts-ignore
      if (!events[event]) return;
      setTimeout(
        // @ts-ignore
        () => events[event].forEach((callback: any) => callback(data)),
        300,
      );
    };

    const subscribe = (
      event: CentrifugoEventType,
      callback: (data: any) => any,
    ) => {
      setEvent(_events => ({
        ..._events,
        // @ts-ignore
        [event]: [...(_events[event] || []), callback],
      }));
    };

    const unsubscribe = (event: CentrifugoEventType) => {
      // @ts-ignore
      if (!events[event]) return;
      setEvent(_events => {
        // @ts-ignore
        delete _events[event];
        return _events;
      });
    };

    return {
      events,
      dispatch,
      subscribe,
      unsubscribe,
    };
  }, [events]);

  return (
    <CentrifugoManagerContext.Provider value={value}>
      {children}
    </CentrifugoManagerContext.Provider>
  );
};

export default CentrifugoManagerHook;

export const ConnectCentrifugoToContainer = () => {
  const centrifugo = useAppSelector(state => state.centrifugo);
  const { dispatch } = useCentrifugoManager();

  useEffect(() => {
    if (!centrifugo.instance || !centrifugo.channels) return;

    if (
      // @ts-ignore
      !centrifugo.instance.getSubscription(centrifugo.channels.notifications)
    ) {
      // @ts-ignore
      const notifySubscriber = centrifugo.instance.newSubscription(
        // @ts-ignore
        centrifugo.channels.notifications,
      );
      notifySubscriber.on('publication', ({ data }: any) =>
        dispatch(CentrifugoEventType.NOTIFICATION_EVENT, data),
      );
      notifySubscriber.subscribe();
    }

    // @ts-ignore
    if (!centrifugo.instance.getSubscription(centrifugo.channels.triggers)) {
      // @ts-ignore
      const triggerSubscriber = centrifugo.instance.newSubscription(
        // @ts-ignore
        centrifugo.channels.triggers,
      );
      triggerSubscriber.on('publication', ({ data }: any) =>
        dispatch(CentrifugoEventType.TRIGGER_EVENT, data),
      );
      triggerSubscriber.subscribe();
    }

    // @ts-ignore
    if (!centrifugo.instance.getSubscription(centrifugo.channels.meta)) {
      // @ts-ignore
      const metaSubscriber = centrifugo.instance.newSubscription(
        // @ts-ignore
        centrifugo.channels.meta,
      );
      metaSubscriber.on('publication', ({ data }: any) =>
        dispatch(CentrifugoEventType.META_EVENT, data),
      );
      metaSubscriber.subscribe();
    }
  }, [centrifugo.instance, dispatch]);

  return <React.Fragment />;
};

export function useCentrifugoManager() {
  return useContext(CentrifugoManagerContext);
}
